import { $t } from '#/locales';
import { isTauri } from '#/utils/platform';

export const SERVER_URL_STORAGE_KEY = 'bayes-mes-server-url';

export const DEFAULT_API_BASE_URL = import.meta.env.VITE_GLOB_API_URL || '';

export function normalizeApiBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

export interface ApiBaseUrlValidation {
  ok: boolean;
  reason?: string;
  parsed?: URL;
}

const LOOPBACK_HOSTS = new Set(['0.0.0.0', '127.0.0.1', '::1', 'localhost']);

function isPrivateHost(hostname: string): boolean {
  if (LOOPBACK_HOSTS.has(hostname)) return true;
  if (hostname.endsWith('.local')) return true;

  const ipv4 = /^(\d{1,3})\.(\d{1,3})\.\d{1,3}\.\d{1,3}$/u.exec(hostname);
  if (!ipv4) return false;

  const a = Number(ipv4[1]);
  const b = Number(ipv4[2]);
  if (a === 10) return true;
  if (a === 127) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  return false;
}

export function validateApiBaseUrl(url: string): ApiBaseUrlValidation {
  const trimmed = normalizeApiBaseUrl(url);
  if (!trimmed) {
    return { ok: false, reason: $t('common.runtimeConfig.addressRequired') };
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return {
      ok: false,
      reason: $t('common.runtimeConfig.invalidFormat'),
    };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return {
      ok: false,
      reason: $t('common.runtimeConfig.unsupportedProtocol'),
    };
  }

  if (!parsed.hostname) {
    return { ok: false, reason: $t('common.runtimeConfig.hostnameMissing') };
  }

  return { ok: true, parsed };
}

export function isInsecureApiBaseUrl(url: string): boolean {
  const result = validateApiBaseUrl(url);
  if (!result.ok || !result.parsed) return false;
  if (result.parsed.protocol !== 'http:') return false;
  return !isPrivateHost(result.parsed.hostname);
}

function getConfiguredApiBaseUrl(): string {
  // 生产包的 Vben 配置文件可以在部署时单独替换，优先读取运行时配置。
  try {
    if (typeof window === 'undefined') {
      return normalizeApiBaseUrl(DEFAULT_API_BASE_URL);
    }

    const runtimeUrl = window._VBEN_ADMIN_PRO_APP_CONF_?.VITE_GLOB_API_URL;
    if (runtimeUrl) return normalizeApiBaseUrl(runtimeUrl);
  } catch {
    // 配置文件不存在时回退到构建时环境变量。
  }

  return normalizeApiBaseUrl(DEFAULT_API_BASE_URL);
}

export function getRuntimeApiBaseUrl(): string {
  const configuredUrl = getConfiguredApiBaseUrl();

  if (!isTauri()) {
    // 相对地址继续走 Web 站点的 /api 反向代理；绝对地址由浏览器直连 API。
    return /^https?:\/\//u.test(configuredUrl) ? configuredUrl : '';
  }

  try {
    return normalizeApiBaseUrl(
      localStorage.getItem(SERVER_URL_STORAGE_KEY) || configuredUrl,
    );
  } catch {
    return configuredUrl;
  }
}

/**
 * 将 API 相对路径解析为当前部署所需的完整地址。
 *
 * Web 开发/同源部署返回相对路径，保留 Vite/Nginx 的 /api 代理；
 * 配置了绝对 API 地址时返回跨域完整 URL，供原生 fetch/XHR 使用。
 */
export function resolveApiUrl(path: string): string {
  const endpoint = path.trim();
  if (!endpoint || /^https?:\/\//u.test(endpoint)) return endpoint;

  const baseUrl = getRuntimeApiBaseUrl();
  if (!baseUrl) return endpoint;

  try {
    return new URL(endpoint, `${baseUrl}/`).toString();
  } catch {
    return endpoint;
  }
}
